"use client";

import {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

const RailEntranceContext = createContext(false);
const SLIDE_DURATION = 300;
const FADE_DURATION = 180;
export function useCoordinatedRailEntrance() {
  return useContext(RailEntranceContext);
}

/** Move shared items first, then fade arrivals into the space they opened. */
export function AccountRailTransition({
  ready,
  fallback,
  children,
}: {
  ready: boolean;
  fallback: React.ReactNode;
  children: React.ReactNode;
}) {
  const rail = useRef<HTMLDivElement>(null);
  const previous = useRef<Map<string, DOMRect> | null>(null);
  const [shown, setShown] = useState(ready);
  const [entering, setEntering] = useState(false);
  const [transitioned, setTransitioned] = useState(false);
  useEffect(() => {
    if (ready === shown) return;
    const items = Array.from(
      rail.current?.querySelectorAll<HTMLElement>("[data-rail-key]") ?? [],
    );
    const change = () => {
      previous.current = new Map(
        items.map((item) => [
          item.dataset.railKey!,
          item.getBoundingClientRect(),
        ]),
      );
      setShown(ready);
      setEntering(true);
      setTransitioned(true);
    };
    if (
      ready ||
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
    ) {
      change();
      return;
    }
    // Fade account-only controls before shared links slide back into place.
    const shared = new Set([
      "organization",
      "separator",
      "Docs",
      "Libum Community",
      "Feedback",
      "updates",
    ]);
    const animations = items
      .filter((item) => !shared.has(item.dataset.railKey!))
      .map((item) =>
        item.animate?.([{ opacity: 1 }, { opacity: 0 }], {
          duration: FADE_DURATION,
          easing: "ease-in-out",
          fill: "forwards",
        }),
      );
    const timeout = window.setTimeout(change, FADE_DURATION);
    return () => {
      window.clearTimeout(timeout);
      animations.forEach((animation) => animation?.cancel());
    };
  }, [ready, shown]);
  useLayoutEffect(() => {
    if (!entering || !previous.current) return;
    const positions = previous.current;
    previous.current = null;
    const animations: Animation[] = [];
    if (!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      rail.current
        ?.querySelectorAll<HTMLElement>("[data-rail-key]")
        .forEach((item) => {
          if (!item.animate) return;
          const before = positions.get(item.dataset.railKey!);
          const after = item.getBoundingClientRect();
          const frames: Keyframe[] = before
            ? [
                {
                  transform: `translate(${before.left - after.left}px, ${before.top - after.top}px)`,
                },
                { transform: "translate(0, 0)" },
              ]
            : [{ opacity: 0 }, { opacity: 1 }];
          animations.push(
            item.animate(frames, {
              duration: before ? SLIDE_DURATION : FADE_DURATION,
              delay: before ? 0 : SLIDE_DURATION,
              easing: "ease-in-out",
              // Keep arrivals invisible throughout the slide, before their fade.
              fill: "backwards",
            }),
          );
        });
    }
    const timeout = window.setTimeout(
      () => setEntering(false),
      SLIDE_DURATION + FADE_DURATION,
    );
    return () => {
      window.clearTimeout(timeout);
      animations.forEach((animation) => animation.cancel());
    };
  }, [entering]);

  return (
    <div
      ref={rail}
      className="relative flex h-full w-12 shrink-0 md:w-[calc(var(--sidebar-width-icon)_+_1px)]"
    >
      {!shown ? (
        fallback
      ) : (
        <RailEntranceContext.Provider value={entering}>
          <div
            className={`flex min-h-0 w-full ${transitioned ? "account-rail-coordinated" : ""} ${entering ? "account-rail-enter" : ""}`}
          >
            {children}
          </div>
        </RailEntranceContext.Provider>
      )}
    </div>
  );
}
